import { Editor, useEditorState } from "@tiptap/react";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { getTableContext, hasMergedCells, isHeaderRow } from "./MentionInputTable.utils.ts";
import { OVERLAY_BLEED } from "./MentionInputTableControls.styles.ts";

export type TableAxis = "row" | "column";

const DRAG_THRESHOLD_PX = 4;

export interface Rect {
    top: number;
    left: number;
    width: number;
    height: number;
}

export interface TableGeometry {
    // The overlay root, relative to the editor wrapper. Everything else is relative to this root.
    overlay: Rect;
    table: Rect;
    row: Rect | null;
    column: Rect | null;
}

export interface TableDragState {
    axis: TableAxis;
    fromIndex: number;
    toIndex: number;
    // Offset along the drag axis of the drop indicator, relative to the overlay root.
    indicatorOffset: number;
}

interface PendingDrag {
    axis: TableAxis;
    fromIndex: number;
    startX: number;
    startY: number;
    dragging: boolean;
}

export function useTableControls(editor: Editor, onFocusChange: (focus: boolean) => void) {
    const tableState = useEditorState({
        editor,
        selector: ({ editor }) => {
            const context = getTableContext(editor.state.selection);
            if (!context) {
                return null;
            }
            return {
                tablePos: context.tablePos,
                caretRowIndex: context.rowIndex,
                caretColumnIndex: context.columnIndex,
                rowCount: context.map.height,
                columnCount: context.map.width,
                hasMergedCells: hasMergedCells(context.table),
                // A header row stays pinned at the top, so it is neither draggable nor a drop target.
                firstMovableRow: isHeaderRow(context.table.child(0)) ? 1 : 0,
                // Any edit inside the table changes its size, which is what should trigger re-measuring.
                tableSize: context.table.nodeSize,
            };
        },
    });

    const overlayRef = useRef<HTMLDivElement>(null);
    const [geometry, setGeometry] = useState<TableGeometry | null>(null);
    const [dragState, setDragState] = useState<TableDragState | null>(null);
    const [menu, setMenu] = useState<{ axis: TableAxis; anchorEl: HTMLElement } | null>(null);
    const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
    const [hoveredColumnIndex, setHoveredColumnIndex] = useState<number | null>(null);
    const pendingDrag = useRef<PendingDrag | null>(null);

    // The handles follow the pointer, falling back to the caret's cell so they are already in place
    // when the user clicks into a table without moving the mouse afterwards.
    const rowIndex = hoveredRowIndex ?? tableState?.caretRowIndex ?? 0;
    const columnIndex = hoveredColumnIndex ?? tableState?.caretColumnIndex ?? 0;
    // A header row has to stay the table's top row, so it cannot be dragged or deleted, and no row may
    // be inserted above it - that would push it down and leave the table without a header.
    const isHeaderRowSelected = !!tableState && rowIndex < tableState.firstMovableRow;
    const isRowDraggable = !!tableState && !isHeaderRowSelected;

    useEffect(() => {
        if (!tableState) {
            return;
        }
        const editorDom = editor.view.dom;
        const trackHover = (event: PointerEvent) => {
            if (pendingDrag.current) {
                return;
            }
            const cell = (event.target as HTMLElement | null)?.closest?.("td, th") as HTMLTableCellElement | null;
            const row = cell?.parentElement as HTMLTableRowElement | null;
            if (!cell || !row) {
                return;
            }
            setHoveredRowIndex(row.rowIndex);
            setHoveredColumnIndex(cell.cellIndex);
        };
        editorDom.addEventListener("pointermove", trackHover);
        return () => editorDom.removeEventListener("pointermove", trackHover);
    }, [tableState, editor]);

    useLayoutEffect(() => {
        if (!tableState) {
            setGeometry(null);
            return;
        }
        const tableElement = getTableElement(editor, tableState.tablePos);
        const measure = () => {
            const overlay = overlayRef.current;
            if (!overlay || !tableElement) {
                setGeometry(null);
                return;
            }
            const wrapperRect = overlay.offsetParent?.getBoundingClientRect() ?? overlay.getBoundingClientRect();
            const editorRect = editor.view.dom.getBoundingClientRect();
            const overlayRect: Rect = {
                top: editorRect.top - wrapperRect.top - OVERLAY_BLEED,
                left: editorRect.left - wrapperRect.left - OVERLAY_BLEED,
                width: editorRect.width + OVERLAY_BLEED * 2,
                height: editorRect.height + OVERLAY_BLEED * 2,
            };
            const origin = { top: wrapperRect.top + overlayRect.top, left: wrapperRect.left + overlayRect.left };
            const rowElement = tableElement.rows[rowIndex];
            const headerCell = tableElement.rows[0]?.cells[columnIndex];
            setGeometry({
                overlay: overlayRect,
                table: relativeTo(tableElement.getBoundingClientRect(), origin),
                row: rowElement ? relativeTo(rowElement.getBoundingClientRect(), origin) : null,
                column: headerCell ? relativeTo(headerCell.getBoundingClientRect(), origin) : null,
            });
        };

        measure();
        const scrollContainer = editor.view.dom;
        scrollContainer.addEventListener("scroll", measure);
        window.addEventListener("resize", measure);
        const resizeObserver = new ResizeObserver(measure);
        if (tableElement) {
            resizeObserver.observe(tableElement);
        }
        return () => {
            scrollContainer.removeEventListener("scroll", measure);
            window.removeEventListener("resize", measure);
            resizeObserver.disconnect();
        };
    }, [tableState, editor, rowIndex, columnIndex]);

    useEffect(() => {
        if (!dragState) {
            return;
        }
        const cancelOnEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                pendingDrag.current = null;
                setDragState(null);
            }
        };
        document.body.style.cursor = "grabbing";
        window.addEventListener("keydown", cancelOnEscape);
        return () => {
            document.body.style.cursor = "";
            window.removeEventListener("keydown", cancelOnEscape);
        };
    }, [dragState]);

    const closeMenu = () => {
        setMenu(null);
        onFocusChange(false);
    };

    const selectLine = (axis: TableAxis, index: number) => {
        const context = getTableContext(editor.state.selection);
        if (!context) {
            return;
        }
        const { map, tableStart, table } = context;
        const first = axis === "row" ? map.positionAt(index, 0, table) : map.positionAt(0, index, table);
        const last = axis === "row" ? map.positionAt(index, map.width - 1, table) : map.positionAt(map.height - 1, index, table);
        editor.commands.setCellSelection({ anchorCell: tableStart + first, headCell: tableStart + last });
    };

    const gripHandlers = (axis: TableAxis, index: number, draggable: boolean) => ({
        onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => {
            // Keep ProseMirror and any outer drag-and-drop context from seeing the gesture.
            event.preventDefault();
            event.stopPropagation();
            selectLine(axis, index);
            if (!draggable) {
                return;
            }
            event.currentTarget.setPointerCapture(event.pointerId);
            pendingDrag.current = { axis, fromIndex: index, startX: event.clientX, startY: event.clientY, dragging: false };
        },
        onPointerMove: (event: React.PointerEvent<HTMLDivElement>) => {
            const pending = pendingDrag.current;
            if (!pending || !tableState) {
                return;
            }
            if (!pending.dragging) {
                const distance = Math.hypot(event.clientX - pending.startX, event.clientY - pending.startY);
                if (distance < DRAG_THRESHOLD_PX) {
                    return;
                }
                pending.dragging = true;
            }
            const target = findDropTarget(editor, tableState, pending, event.clientX, event.clientY, overlayRef.current);
            if (target) {
                setDragState(target);
            }
        },
        onPointerUp: (event: React.PointerEvent<HTMLDivElement>) => {
            const pending = pendingDrag.current;
            pendingDrag.current = null;
            if (!pending?.dragging) {
                setMenu({ axis, anchorEl: event.currentTarget });
                onFocusChange(true);
                return;
            }
            const drop = dragState;
            setDragState(null);
            if (drop && drop.toIndex !== drop.fromIndex) {
                if (axis === "row") {
                    editor.chain().focus().moveTableRow(drop.fromIndex, drop.toIndex).run();
                    setHoveredRowIndex(drop.toIndex);
                } else {
                    editor.chain().focus().moveTableColumn(drop.fromIndex, drop.toIndex).run();
                    setHoveredColumnIndex(drop.toIndex);
                }
            }
        },
        onPointerCancel: () => {
            pendingDrag.current = null;
            setDragState(null);
        },
    });

    const runAndClose = (run: () => void) => {
        run();
        closeMenu();
    };

    const actions = {
        addRowAtEnd: () => editor.chain().focus().addRowAtEnd().run(),
        addColumnAtEnd: () => editor.chain().focus().addColumnAtEnd().run(),
        insertRowAbove: () => runAndClose(() => editor.chain().focus().addRowBefore().run()),
        insertRowBelow: () => runAndClose(() => editor.chain().focus().addRowAfter().run()),
        insertColumnLeft: () => runAndClose(() => editor.chain().focus().addColumnBefore().run()),
        insertColumnRight: () => runAndClose(() => editor.chain().focus().addColumnAfter().run()),
        deleteRow: () => runAndClose(() => editor.chain().focus().deleteRow().run()),
        deleteColumn: () => runAndClose(() => editor.chain().focus().deleteColumn().run()),
        deleteTable: () => runAndClose(() => editor.chain().focus().deleteTable().run()),
        moveRow: (delta: number) =>
            runAndClose(() => {
                editor
                    .chain()
                    .focus()
                    .moveTableRow(rowIndex, rowIndex + delta)
                    .run();
                setHoveredRowIndex(rowIndex + delta);
            }),
        moveColumn: (delta: number) =>
            runAndClose(() => {
                editor
                    .chain()
                    .focus()
                    .moveTableColumn(columnIndex, columnIndex + delta)
                    .run();
                setHoveredColumnIndex(columnIndex + delta);
            }),
    };

    return {
        tableState,
        geometry,
        dragState,
        menu,
        overlayRef,
        rowIndex,
        columnIndex,
        isRowDraggable,
        gripHandlers,
        closeMenu,
        actions,
        canInsertRowAbove: !isHeaderRowSelected,
        canDeleteRow: !isHeaderRowSelected,
        canMoveRowUp: isRowDraggable && rowIndex > (tableState?.firstMovableRow ?? 0),
        canMoveRowDown: isRowDraggable && !!tableState && rowIndex < tableState.rowCount - 1,
        canMoveColumnLeft: columnIndex > 0,
        canMoveColumnRight: !!tableState && columnIndex < tableState.columnCount - 1,
    };
}

function getTableElement(editor: Editor, tablePos: number): HTMLTableElement | null {
    const dom = editor.view.nodeDOM(tablePos);
    if (!(dom instanceof HTMLElement)) {
        return null;
    }
    return dom instanceof HTMLTableElement ? dom : dom.querySelector("table");
}

function relativeTo(rect: DOMRect, origin: { top: number; left: number }): Rect {
    return { top: rect.top - origin.top, left: rect.left - origin.left, width: rect.width, height: rect.height };
}

// Finds the boundary the pointer is nearest to, expressed as the index the dragged line would end up
// at after the move, plus where to draw the indicator.
function findDropTarget(
    editor: Editor,
    tableState: NonNullable<ReturnType<typeof useTableControls>["tableState"]>,
    pending: PendingDrag,
    clientX: number,
    clientY: number,
    overlay: HTMLDivElement | null
): TableDragState | null {
    const tableElement = getTableElement(editor, tableState.tablePos);
    if (!tableElement || !overlay) {
        return null;
    }
    const overlayRect = overlay.getBoundingClientRect();
    const isRow = pending.axis === "row";
    const lines = isRow ? Array.from(tableElement.rows) : Array.from(tableElement.rows[0]?.cells ?? []);
    const rects = lines.map((line) => line.getBoundingClientRect());
    const pointer = isRow ? clientY : clientX;
    const minBoundary = isRow ? tableState.firstMovableRow : 0;

    let boundary = rects.length;
    for (let index = 0; index < rects.length; index++) {
        const middle = isRow ? rects[index].top + rects[index].height / 2 : rects[index].left + rects[index].width / 2;
        if (pointer < middle) {
            boundary = index;
            break;
        }
    }
    boundary = Math.max(boundary, minBoundary);

    const toIndex = boundary > pending.fromIndex ? boundary - 1 : boundary;
    const indicatorClient =
        boundary < rects.length ? (isRow ? rects[boundary].top : rects[boundary].left) : isRow ? rects[rects.length - 1].bottom : rects[rects.length - 1].right;
    const indicatorOffset = indicatorClient - (isRow ? overlayRect.top : overlayRect.left);

    return { axis: pending.axis, fromIndex: pending.fromIndex, toIndex, indicatorOffset };
}
